/**
 * AI Clinical Notes Service
 *
 * Generates AI-assisted clinical note suggestions from session transcripts.
 *
 * KEY PRINCIPLE: AI suggestions are ASSISTIVE ONLY - providers have complete
 * control to edit, modify, or discard all suggestions.
 *
 * Features:
 * - Generate clinical notes from transcripts
 * - Multiple note templates (SOAP, DAP, BIRP, Progress)
 * - Provider-controlled editing
 * - Edit history tracking
 * - Version control
 */

import { FirebaseClinicalNotesService } from './firebase/FirebaseClinicalNotesService';

export type NoteTemplate = 'SOAP' | 'DAP' | 'BIRP' | 'Progress' | 'Initial' | 'Custom';
export type NoteStatus = 'draft' | 'final' | 'signed';
export type ChangeType = 'ai-generated' | 'manual-edit' | 'template-insert' | 'regenerated';

export interface NoteEdit {
  timestamp: Date;
  editorId: string;  // Provider ID
  changeType: ChangeType;
  previousContent?: string;
  description?: string;
}

export interface ClinicalNote {
  id: string;
  sessionId: string;
  patientId: string;
  providerId: string;
  template: NoteTemplate;
  content: string;  // Final provider-approved content
  aiGeneratedContent?: string;  // Original AI suggestion (preserved for reference)
  wasAiAssisted: boolean;
  providerEdited: boolean;
  version: number;
  editHistory: NoteEdit[];
  createdAt: Date;
  lastEditedAt: Date;
  signedBy?: string;
  signedAt?: Date;
  status: NoteStatus;
  customInstructions?: string;  // Provider's custom instructions for AI
}

export interface ClinicalNoteSuggestion {
  template: NoteTemplate;
  content: string;
  sections: NoteSections;
  confidence: number;  // 0-1 scale
  sources: string[];  // Transcript excerpts used
  generatedAt: Date;
  warnings?: string[];  // e.g., "Low confidence in assessment section"
}

export interface NoteSections {
  // SOAP format
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;

  // DAP format
  data?: string;
  // assessment (shared)
  // plan (shared)

  // BIRP format
  behavior?: string;
  intervention?: string;
  response?: string;
  // plan (shared)

  // Progress Note format
  progress?: string;
  goals?: string;
  barriers?: string;
  nextSteps?: string;

  // Initial Assessment
  presentingProblem?: string;
  history?: string;
  mentalStatus?: string;
  diagnosis?: string;
  recommendations?: string;
}

export interface GenerateNoteOptions {
  transcript: string;
  patientHistory?: {
    diagnoses?: string[];
    medications?: string[];
    previousNotes?: string[];
  };
  template: NoteTemplate;
  customInstructions?: string;
  sessionType?: string;
  sessionDuration?: number;
}

class AIClinicalNotesService {
  private readonly STORAGE_KEY = 'clinical_notes';
  private notes: Map<string, ClinicalNote> = new Map();

  private readonly API_ENDPOINT = '/api/generate-notes';
  private readonly USE_MOCK = false; // Set to true to use mock responses for development

  constructor() {
    this.loadNotes();
  }

  /**
   * Generate AI-assisted clinical note suggestion
   *
   * IMPORTANT: This is a SUGGESTION only. Provider must review and edit.
   */
  async generateNoteSuggestion(options: GenerateNoteOptions): Promise<ClinicalNoteSuggestion> {
    try {
      console.log('Generating AI note suggestion...', options.template);

      if (this.USE_MOCK) {
        // Mock AI response for development
        return this.generateMockSuggestion(options);
      }

      // Call GPT-4 via server-side API (API key is server-side only)
      const { firebaseAuthService } = await import('./firebase/FirebaseAuthService');
      const token = await firebaseAuthService.getIdToken();
      if (!token) {
        console.warn('No auth token available, falling back to mock');
        return this.generateMockSuggestion(options);
      }

      const response = await fetch(this.API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          transcript: options.transcript,
          patientHistory: options.patientHistory,
          template: options.template,
          customInstructions: options.customInstructions,
          sessionType: options.sessionType,
          sessionDuration: options.sessionDuration,
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `AI service returned ${response.status}`);
      }

      const suggestion = await response.json();
      return {
        template: suggestion.template || options.template,
        content: suggestion.content || '',
        sections: suggestion.sections || {},
        confidence: suggestion.confidence || 0.85,
        sources: suggestion.sources || [],
        generatedAt: new Date(suggestion.generatedAt || Date.now()),
        warnings: suggestion.warnings || [],
      };

    } catch (error) {
      console.error('Failed to generate AI suggestion:', error);
      // Fallback to mock if API fails
      return this.generateMockSuggestion(options);
    }
  }

  /**
   * Generate mock AI suggestion for development/testing
   */
  private generateMockSuggestion(options: GenerateNoteOptions): ClinicalNoteSuggestion {
    const { template, transcript, sessionType, sessionDuration } = options;

    // Extract some basic info from transcript
    const words = transcript.split(' ').length;
    const duration = sessionDuration ? `${Math.floor(sessionDuration / 60)} minutes` : 'N/A';

    let sections: NoteSections = {};
    let content = '';

    switch (template) {
      case 'SOAP':
        sections = {
          subjective: `Patient reports experiencing symptoms as discussed during ${duration} ${sessionType || 'therapy'} session. Key concerns include mood fluctuations and stress management challenges. Patient appears motivated to engage in treatment.`,

          objective: `Patient presented as appropriately dressed and well-groomed. Eye contact was good. Speech was normal in rate and tone. Mood appeared congruent with affect. No signs of acute distress observed during session. Patient was cooperative and engaged throughout.`,

          assessment: `Patient demonstrates continued progress in identifying triggers and utilizing coping strategies. Symptoms appear consistent with previous diagnoses. Patient shows insight into their condition and is actively participating in treatment planning. Risk assessment indicates no current safety concerns.`,

          plan: `Continue current treatment approach with weekly sessions. Patient will practice mindfulness techniques discussed today. Review progress on goals at next session. Monitor mood and stress levels. Next appointment scheduled for one week.`
        };

        content = `SOAP Note\n\nSubjective:\n${sections.subjective}\n\nObjective:\n${sections.objective}\n\nAssessment:\n${sections.assessment}\n\nPlan:\n${sections.plan}`;
        break;

      case 'DAP':
        sections = {
          data: `Session duration: ${duration}. Patient attended scheduled ${sessionType || 'therapy'} session. Patient discussed recent experiences and challenges. Observed patient's engagement level and emotional responses throughout session. Patient completed homework assignment from previous session.`,

          assessment: `Patient continues to make progress toward treatment goals. Demonstrates improved coping skills and self-awareness. Current symptoms appear manageable with continued treatment. Patient's participation and motivation remain strong.`,

          plan: `Continue weekly individual therapy sessions. Assign new therapeutic exercises to practice between sessions. Review and update treatment goals as needed. Schedule follow-up in one week.`
        };

        content = `DAP Note\n\nData:\n${sections.data}\n\nAssessment:\n${sections.assessment}\n\nPlan:\n${sections.plan}`;
        break;

      case 'BIRP':
        sections = {
          behavior: `Patient arrived on time for scheduled session. Demonstrated appropriate social interaction. Shared experiences and concerns openly. Mood appeared stable with appropriate affect.`,

          intervention: `Utilized cognitive-behavioral techniques to address identified concerns. Provided psychoeducation regarding stress management and coping strategies. Facilitated discussion of treatment goals and progress. Offered supportive counseling and active listening.`,

          response: `Patient engaged actively in therapeutic interventions. Demonstrated understanding of concepts discussed. Reported finding techniques helpful and expressed willingness to practice between sessions. Patient appeared receptive to feedback.`,

          plan: `Patient will practice coping strategies discussed in session. Continue weekly therapy appointments. Monitor progress on current goals. Follow up on implementation of techniques at next session.`
        };

        content = `BIRP Note\n\nBehavior:\n${sections.behavior}\n\nIntervention:\n${sections.intervention}\n\nResponse:\n${sections.response}\n\nPlan:\n${sections.plan}`;
        break;

      case 'Progress':
        sections = {
          progress: `Patient continues to demonstrate progress toward established treatment goals. Reports implementing strategies discussed in previous sessions with positive results. Shows increased self-awareness and improved symptom management.`,

          goals: `1. Continue developing healthy coping mechanisms for stress\n2. Improve emotional regulation skills\n3. Enhance self-care practices\n4. Strengthen support network`,

          barriers: `Patient reports time constraints as occasional barrier to consistent practice of techniques. Working to address scheduling challenges. No significant barriers to treatment progress at this time.`,

          nextSteps: `Patient will continue practicing techniques between sessions. Will work on identified goals and report progress at next appointment. Scheduled for follow-up in one week.`
        };

        content = `Progress Note\n\nProgress:\n${sections.progress}\n\nGoals:\n${sections.goals}\n\nBarriers:\n${sections.barriers}\n\nNext Steps:\n${sections.nextSteps}`;
        break;

      case 'Initial':
        sections = {
          presentingProblem: `Patient presents for initial assessment seeking support for managing stress and mood concerns. Reports symptoms have been ongoing for several months and are impacting daily functioning.`,

          history: `Patient reports relevant history as discussed. No previous mental health treatment reported. Denies current substance use. Medical history unremarkable.`,

          mentalStatus: `Patient alert and oriented x4. Appearance appropriate. Speech normal. Thought process logical and goal-directed. Mood described as anxious. Affect congruent. No hallucinations or delusions reported. Insight and judgment fair.`,

          diagnosis: `Provisional diagnosis pending additional information and assessment. Will continue to evaluate symptoms and functioning over coming sessions.`,

          recommendations: `Recommend weekly individual therapy utilizing evidence-based approaches. Patient agreeable to treatment plan. Will reassess diagnosis and treatment approach as appropriate. Patient verbalized understanding and agreement with recommendations.`
        };

        content = `Initial Assessment\n\nPresenting Problem:\n${sections.presentingProblem}\n\nHistory:\n${sections.history}\n\nMental Status Exam:\n${sections.mentalStatus}\n\nDiagnosis:\n${sections.diagnosis}\n\nRecommendations:\n${sections.recommendations}`;
        break;

      default:
        content = `Session Note\n\nPatient attended ${duration} ${sessionType || 'therapy'} session. Discussed current concerns and progress toward treatment goals. Patient engaged appropriately throughout session. Plan to continue current treatment approach with follow-up scheduled.`;
    }

    return {
      template,
      content,
      sections,
      confidence: 0.75, // Mock confidence score
      sources: [
        transcript.substring(0, 100) + '...',
        transcript.substring(Math.floor(transcript.length / 2), Math.floor(transcript.length / 2) + 100) + '...'
      ],
      generatedAt: new Date(),
      warnings: words < 50 ? ['Transcript may be too short for accurate note generation'] : undefined
    };
  }

  /**
   * Create a new clinical note (with or without AI assistance)
   */
  async createNote(
    sessionId: string,
    patientId: string,
    providerId: string,
    template: NoteTemplate,
    content: string,
    wasAiAssisted: boolean = false,
    aiGeneratedContent?: string
  ): Promise<ClinicalNote> {
    const noteId = this.generateNoteId();

    const note: ClinicalNote = {
      id: noteId,
      sessionId,
      patientId,
      providerId,
      template,
      content,
      aiGeneratedContent,
      wasAiAssisted,
      providerEdited: wasAiAssisted, // If AI-assisted, provider should edit
      version: 1,
      editHistory: [{
        timestamp: new Date(),
        editorId: providerId,
        changeType: wasAiAssisted ? 'ai-generated' : 'manual-edit',
        description: wasAiAssisted ? 'AI-generated initial draft' : 'Created manually'
      }],
      createdAt: new Date(),
      lastEditedAt: new Date(),
      status: 'draft'
    };

    this.notes.set(noteId, note);
    this.saveNotes();

    // Persist to Firestore for server-side durability
    try {
      const firestoreId = await FirebaseClinicalNotesService.createNote({
        sessionId,
        patientId,
        providerId,
        template,
        content,
        aiGeneratedContent,
        wasAiAssisted,
        providerEdited: wasAiAssisted,
        version: 1,
        editHistory: [{
          timestamp: new Date().toISOString(),
          editorId: providerId,
          changeType: wasAiAssisted ? 'ai-generated' : 'manual-edit',
          description: wasAiAssisted ? 'AI-generated initial draft' : 'Created manually'
        }],
        status: 'draft'
      });
      // Store the Firestore ID so updates can find it
      note.id = firestoreId;
      this.notes.delete(noteId);
      this.notes.set(firestoreId, note);
      this.saveNotes();
      console.log('Clinical note created (Firestore):', firestoreId);
    } catch (err) {
      console.warn('Firestore save failed, note saved locally:', err);
    }

    return note;
  }

  /**
   * Update existing note (provider editing)
   */
  async updateNote(
    noteId: string,
    newContent: string,
    providerId: string
  ): Promise<ClinicalNote> {
    const note = this.notes.get(noteId);
    if (!note) {
      throw new Error('Note not found');
    }

    // Track edit history
    note.editHistory.push({
      timestamp: new Date(),
      editorId: providerId,
      changeType: 'manual-edit',
      previousContent: note.content
    });

    // Update note
    note.content = newContent;
    note.providerEdited = true;
    note.lastEditedAt = new Date();
    note.version += 1;

    this.notes.set(noteId, note);
    this.saveNotes();

    // Persist update to Firestore
    try {
      await FirebaseClinicalNotesService.updateNote(noteId, { content: newContent }, providerId, 'manual-edit');
    } catch (err) {
      console.warn('Firestore update failed, note updated locally:', err);
    }

    console.log('Clinical note updated:', noteId);
    return note;
  }

  /**
   * Sign off on note (finalizes it)
   */
  async signNote(noteId: string, providerId: string): Promise<ClinicalNote> {
    const note = this.notes.get(noteId);
    if (!note) {
      throw new Error('Note not found');
    }

    note.status = 'signed';
    note.signedBy = providerId;
    note.signedAt = new Date();

    this.notes.set(noteId, note);
    this.saveNotes();

    // Persist sign to Firestore
    try {
      await FirebaseClinicalNotesService.signNote(noteId, providerId);
    } catch (err) {
      console.warn('Firestore sign failed, note signed locally:', err);
    }

    console.log('Clinical note signed:', noteId);
    return note;
  }

  /**
   * Get note by ID
   */
  async getNote(noteId: string): Promise<ClinicalNote | null> {
    return this.notes.get(noteId) || null;
  }

  /**
   * Get notes for a session
   */
  async getNotesBySession(sessionId: string): Promise<ClinicalNote[]> {
    return Array.from(this.notes.values())
      .filter(note => note.sessionId === sessionId)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  /**
   * Get notes for a patient
   */
  async getNotesByPatient(patientId: string): Promise<ClinicalNote[]> {
    return Array.from(this.notes.values())
      .filter(note => note.patientId === patientId)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  /**
   * Delete note
   */
  async deleteNote(noteId: string): Promise<void> {
    this.notes.delete(noteId);
    this.saveNotes();
    console.log('Clinical note deleted:', noteId);
  }

  /**
   * Get edit history for a note
   */
  async getNoteHistory(noteId: string): Promise<NoteEdit[]> {
    const note = this.notes.get(noteId);
    return note?.editHistory || [];
  }

  // Private helper methods

  private generateNoteId(): string {
    return `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private loadNotes(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const notesArray = JSON.parse(stored);
        this.notes = new Map(
          notesArray.map((note: any) => {
            // Convert date strings back to Date objects
            note.createdAt = new Date(note.createdAt);
            note.lastEditedAt = new Date(note.lastEditedAt);
            if (note.signedAt) note.signedAt = new Date(note.signedAt);
            note.editHistory = note.editHistory.map((edit: any) => ({
              ...edit,
              timestamp: new Date(edit.timestamp)
            }));
            return [note.id, note];
          })
        );
        console.log(`Loaded ${this.notes.size} clinical notes`);
      }
    } catch (error) {
      console.error('Failed to load clinical notes:', error);
    }
  }

  private saveNotes(): void {
    try {
      const notesArray = Array.from(this.notes.values());
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(notesArray));
    } catch (error) {
      console.error('Failed to save clinical notes:', error);
    }
  }
}

// Export singleton instance
export const aiClinicalNotesService = new AIClinicalNotesService();
export default AIClinicalNotesService;
